/**
 * "CSV format guide" affordance for Step 1 of the Upload Wizard.
 *
 * Renders a small info-icon button that opens a modal dialog with the full
 * CSV-preparation guide (column format, channel keys, verbatim formatting
 * rules, file limits, validation). The guide content lives in
 * `csvFormatGuide.ts` as markdown and is rendered with react-markdown +
 * remark-gfm (the `NarrativeBlock` pattern) so the columns table renders.
 *
 * Self-contained: no props, manages its own open state via the uncontrolled
 * Radix Dialog.
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Info } from 'lucide-react';

import { Button } from '../../../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../../ui/dialog';
import { CSV_FORMAT_GUIDE_MD } from './csvFormatGuide';

const CsvFormatGuideDialog: React.FC = () => {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5 text-muted-foreground"
          aria-label="Open CSV format guide"
          data-testid="csv-format-guide-trigger"
        >
          <Info className="h-4 w-4" aria-hidden="true" />
          CSV format guide
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>CSV format guide</DialogTitle>
          <DialogDescription>
            How to prepare a CSV for upload into this collection.
          </DialogDescription>
        </DialogHeader>
        <div
          className="prose prose-sm max-w-none max-h-[70vh] overflow-y-auto pr-1 [&_pre]:whitespace-pre-wrap [&_pre]:break-words prose-code:before:content-none prose-code:after:content-none"
          data-testid="csv-format-guide-body"
        >
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {CSV_FORMAT_GUIDE_MD}
          </ReactMarkdown>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CsvFormatGuideDialog;
